import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Plus, Upload, Sparkles, Trash2, ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../hooks/useTranslation';
import { Button } from '../components/ui/button';
import { TodayFollowUpsWidget } from '../components/ai-lead-scanner/TodayFollowUpsWidget';
import { DiscardSuggestionsWidget } from '../components/ai-lead-scanner/DiscardSuggestionsWidget';
import { LeadFilters } from '../components/ai-lead-scanner/LeadFilters';
import { LeadTable } from '../components/ai-lead-scanner/LeadTable';
import { LeadCalendar } from '../components/ai-lead-scanner/LeadCalendar';
import { AddLeadModal } from '../components/ai-lead-scanner/AddLeadModal';
import { ImportExcelModal } from '../components/ai-lead-scanner/ImportExcelModal';
import { DiscardLeadsDialog } from '../components/ai-lead-scanner/DiscardLeadsDialog';

const PAGE_SIZE = 50;

// Dashboard principal del AI Lead Scanner (Commit 14). Las 3 sub-entregas
// de este commit ya están completas: dashboard+tabla, ficha de lead, y
// calendario+modales (añadir lead manual, importar Excel con doble
// confirmación dry_run — Commit 12).
export function LeadScannerPage() {
  const { api } = useAuth();
  const { t } = useTranslation();

  const [leads, setLeads] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  // Filtros persistidos en la URL (?search=...&status=...) — decisión de
  // arquitectura 2026-07-09 (docs/adam-intelligence-spec.md), primer uso de
  // useSearchParams en el proyecto. Sobreviven a navegar al detalle de un
  // lead y volver, a diferencia de un useState local que se pierde al
  // desmontar/remontar este componente. offset/selectedIds siguen locales
  // — fuera de alcance de esta mejora, solo se pidió persistir filtros.
  const [searchParams, setSearchParams] = useSearchParams();

  // exclude_terminal_status:true por defecto — oculta DISCARDED (y otros
  // estados terminales) de la vista de trabajo diaria. Ahora que DISCARDED
  // acumula tanto descartes automáticos del scoring como manuales, sin
  // esto la tabla principal se llenaría de leads que ya no requieren
  // acción. status explícito sigue teniendo prioridad sobre esto en el
  // backend (Commit 13.2) — si Marta filtra por status="DISCARDED" a
  // propósito, los ve igual. También se desactiva si hay un término de
  // search activo (bug real 2026-07-13: buscar por nombre no encontraba
  // leads ya descartados, impidiendo detectar duplicados antes de añadir
  // uno nuevo) — con texto en el buscador, Marta está verificando si algo
  // ya existe, no filtrando su cola de trabajo diaria. Nunca se guarda en
  // la URL — se deriva aquí en cada lectura a partir de si hay status
  // explícito o search, igual que antes se derivaba en cada escritura.
  const filters = useMemo(() => {
    const status = searchParams.get('status') || undefined;
    const search = searchParams.get('search') || undefined;
    const minScoreRaw = searchParams.get('min_score');
    return {
      search,
      sector: searchParams.get('sector') || undefined,
      city: searchParams.get('city') || undefined,
      priority: searchParams.get('priority') || undefined,
      status,
      temperature: searchParams.get('temperature') || undefined,
      min_score: minScoreRaw !== null && minScoreRaw !== '' ? Number(minScoreRaw) : undefined,
      // Con un status explícito o un término de búsqueda activo, no se
      // oculta DISCARDED/NOT_INTERESTED — Marta necesita poder encontrar
      // leads descartados al buscar por nombre para detectar duplicados
      // antes de añadir uno nuevo (bug real: buscar no los encontraba).
      exclude_terminal_status: (status || search) ? undefined : true,
    };
  }, [searchParams]);

  const [selectedIds, setSelectedIds] = useState(new Set());
  const [analyzing, setAnalyzing] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [addLeadOpen, setAddLeadOpen] = useState(false);
  const [importExcelOpen, setImportExcelOpen] = useState(false);
  const [discarding, setDiscarding] = useState(false);
  const [discardDialogOpen, setDiscardDialogOpen] = useState(false);

  const fetchLeads = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/leads', {
        params: { ...filters, limit: PAGE_SIZE, offset },
      });
      setLeads(res.data.leads || []);
      setTotal(res.data.total || 0);
    } catch (error) {
      console.error('Failed to fetch leads:', error);
      toast.error(t('aiLeadScanner.table.loadError'));
      setLeads([]);
    } finally {
      setLoading(false);
    }
  }, [api, filters, offset, t]);

  useEffect(() => {
    fetchLeads();
  }, [fetchLeads]);

  // Cambiar filtros reinicia la paginación — offset=0 con un filtro nuevo
  // aplicado tiene sentido, mantener el offset anterior no.
  //
  // LeadFilters.jsx sustituye el objeto de filtros COMPLETO en cada cambio
  // (no lo combina con el anterior) — nunca incluye exclude_terminal_status
  // porque no es un campo que gestione ese componente; el `filters` de
  // arriba ya lo deriva en cada lectura de la URL, no hace falta escribirlo
  // aquí. { replace: true } para no apilar una entrada de historial por
  // cada tecla del debounce de LeadFilters — solo la navegación real al
  // detalle de un lead debe crear una entrada nueva.
  const handleFiltersChange = useCallback((newFilters) => {
    const next = new URLSearchParams();
    Object.entries(newFilters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        next.set(key, String(value));
      }
    });
    setSearchParams(next, { replace: true });
    setOffset(0);
    setSelectedIds(new Set());
  }, [setSearchParams]);

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    setSelectedIds((prev) => {
      const allSelected = leads.length > 0 && leads.every((lead) => prev.has(lead.id));
      return allSelected ? new Set() : new Set(leads.map((lead) => lead.id));
    });
  };

  // Secuencial, no en paralelo — mismo criterio que el resto del módulo
  // para llamadas que cuestan LLM (nightly_analyze.py, Commit 10): evitar
  // ráfagas simultáneas de análisis. Un fallo en un lead no detiene el resto.
  const handleAnalyzeSelected = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;

    setAnalyzing(true);
    let succeeded = 0;
    let failed = 0;

    for (const id of ids) {
      try {
        await api.post(`/leads/${id}/analyze`);
        succeeded += 1;
      } catch (error) {
        console.error(`Failed to analyze lead ${id}:`, error);
        failed += 1;
      }
    }

    setAnalyzing(false);
    setSelectedIds(new Set());
    setRefreshKey((k) => k + 1);
    await fetchLeads();

    if (failed === 0) {
      toast.success(`${succeeded} lead(s) analizados correctamente`);
    } else {
      toast.warning(`${succeeded} analizados, ${failed} con error`);
    }
  };

  // Borrado suave en lote — mismo patrón secuencial que handleAnalyzeSelected
  // (mismos volúmenes de selección, mismo criterio de no ráfagas paralelas),
  // reutilizando POST /leads/{id}/status uno por uno en vez de un endpoint
  // de lote nuevo — decisión explícita, ver mensaje de commit. Gateado por
  // el diálogo de confirmación, nunca se llama directamente desde el botón.
  const handleDiscardSelected = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;

    setDiscarding(true);
    let succeeded = 0;
    let failed = 0;

    for (const id of ids) {
      try {
        await api.post(`/leads/${id}/status`, { status: 'DISCARDED' });
        succeeded += 1;
      } catch (error) {
        console.error(`Failed to discard lead ${id}:`, error);
        failed += 1;
      }
    }

    setDiscarding(false);
    setDiscardDialogOpen(false);
    setSelectedIds(new Set());
    await fetchLeads();

    if (failed === 0) {
      toast.success(`${succeeded} lead(s) descartados correctamente`);
    } else {
      toast.warning(`${succeeded} descartados, ${failed} con error`);
    }
  };

  const pageInfo = useMemo(() => {
    const from = total === 0 ? 0 : offset + 1;
    const to = Math.min(offset + PAGE_SIZE, total);
    return { from, to };
  }, [offset, total]);

  return (
    <div className="p-4 sm:p-6 max-w-[1400px] mx-auto h-full overflow-y-auto">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-xl font-semibold text-zinc-100">{t('aiLeadScanner.title')}</h1>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setAddLeadOpen(true)}
            data-testid="open-add-lead-modal"
          >
            <Plus className="w-4 h-4 mr-1.5" />
            {t('aiLeadScanner.actions.addLead')}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setImportExcelOpen(true)}
            data-testid="open-import-excel-modal"
          >
            <Upload className="w-4 h-4 mr-1.5" />
            {t('aiLeadScanner.actions.importExcel')}
          </Button>
          <Button
            size="sm"
            disabled={selectedIds.size === 0 || analyzing}
            onClick={handleAnalyzeSelected}
            data-testid="analyze-selected-button"
          >
            {analyzing ? (
              <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
            ) : (
              <Sparkles className="w-4 h-4 mr-1.5" />
            )}
            {t('aiLeadScanner.actions.analyzeSelected')}
            {selectedIds.size > 0 && ` (${selectedIds.size})`}
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={selectedIds.size === 0 || discarding}
            onClick={() => setDiscardDialogOpen(true)}
            data-testid="discard-selected-button"
          >
            <Trash2 className="w-4 h-4 mr-1.5" />
            {t('aiLeadScanner.actions.discardSelected')}
            {selectedIds.size > 0 && ` (${selectedIds.size})`}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <TodayFollowUpsWidget refreshKey={refreshKey} />
        <DiscardSuggestionsWidget refreshKey={refreshKey} />
      </div>

      <LeadFilters initialFilters={filters} onFiltersChange={handleFiltersChange} />

      <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
        <LeadTable
          leads={leads}
          loading={loading}
          selectedIds={selectedIds}
          onToggleSelect={toggleSelect}
          onToggleSelectAll={toggleSelectAll}
        />
      </div>

      {!loading && total > 0 && (
        <div className="flex items-center justify-between mt-4">
          <p className="text-sm text-zinc-500">
            {t('aiLeadScanner.pagination.showing', { from: pageInfo.from, to: pageInfo.to, total })}
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={offset === 0}
              onClick={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))}
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              {t('aiLeadScanner.pagination.previous')}
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={offset + PAGE_SIZE >= total}
              onClick={() => setOffset((o) => o + PAGE_SIZE)}
            >
              {t('aiLeadScanner.pagination.next')}
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>
      )}

      <h2 className="text-sm font-semibold text-zinc-400 uppercase tracking-wide mt-8 mb-3">
        {t('aiLeadScanner.calendar.title')}
      </h2>
      <LeadCalendar />

      <AddLeadModal
        open={addLeadOpen}
        onOpenChange={setAddLeadOpen}
        onCreated={fetchLeads}
      />

      <ImportExcelModal
        open={importExcelOpen}
        onOpenChange={setImportExcelOpen}
        onImported={fetchLeads}
      />

      <DiscardLeadsDialog
        open={discardDialogOpen}
        onOpenChange={setDiscardDialogOpen}
        count={selectedIds.size}
        onConfirm={handleDiscardSelected}
        confirming={discarding}
      />
    </div>
  );
}
